'use client'

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/button'
import { ArrowRightIcon } from '@heroicons/react/24/solid'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'

interface NavigationItem {
  name: string
  href: string
}

const navigation: NavigationItem[] = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'Our Work', href: '/our-work' },
  { name: 'About Us', href: '/about' },
  { name: 'FAQ', href: '/faq' },
]

function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ')
}

export function Header({ isBannerVisible }: { isBannerVisible: boolean }) {
  const pathname = usePathname()

  return (
    <>
      <Disclosure as="nav">
        {({ open }) => (
          <div className={`fixed z-40 left-0 right-0 w-full ${isBannerVisible ? 'top-0 md:top-[36px]' : 'top-0'}`}>
            <div
              className={classNames(
                'will-change-transform bg-white',
                open
                  ? 'md:bg-white bg-white backdrop-blur-md'
                  : 'bg-white'
              )}
            >
              <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
                <div className="flex h-14 items-center justify-between">
                  {/* Left side: logo */}
                  <div className="flex items-center">
                    <div className="flex shrink-0 items-center">
                      <Link href="/">
                        <Image
                          alt="SCA Logo"
                          src="/logos/sca.svg"
                          width={32}
                          height={32}
                          className={classNames(
                            "h-8 w-auto transition-transform duration-500 ease-out hover:scale-105 will-change-transform",
                            "brightness-100 invert-0"
                          )}
                          style={{ filter: 'brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)' }}
                        />
                      </Link>
                    </div>
                  </div>

                  {/* Center: Desktop nav links */}
                  <div className="hidden md:flex md:items-center md:space-x-4">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        aria-current={pathname === item.href ? 'page' : undefined}
                          className={classNames(
                            pathname === item.href
                              ? 'bg-[#171A20]/5'
                              : 'hover:bg-[#171A20]/5',
                            'rounded px-4 py-1 text-sm font-bold text-center transition-all duration-300 ease-out'
                          )}
                        style={{ color: 'rgb(23, 26, 32)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif' }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>

                  {/* Right side: contact, phone, client portal, menu toggle (mobile) */}
                  <div className="flex items-center space-x-4">
                    <div className="hidden md:flex md:items-center md:space-x-4">
                      <Button
                        asChild
                        variant="default"
                        className="font-semibold transition-transform duration-500 ease-out hover:scale-105 will-change-transform h-10 px-4"
                        style={{ backgroundColor: '#171A20', color: 'white' }}
                      >
                        <a href="/client-portal" className="text-center">
                          Client Portal
                        </a>
                      </Button>
                    </div>
                    
                    {/* Mobile menu button */}
                    <div className="flex items-center md:hidden">
                      <DisclosureButton 
                        className={classNames(
                          "group relative inline-flex items-center justify-center rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-inset transition-all duration-300 ease-out font-inter",
                          "hover:bg-[#171A20]/5 focus:ring-[#171A20]"
                        )}
                        style={{ color: '#171A20' }}
                      >
                        <span className="absolute -inset-0.5" />
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon
                          aria-hidden="true"
                          className="block h-6 w-6 group-data-[open]:hidden"
                        />
                        <XMarkIcon
                          aria-hidden="true"
                          className="hidden h-6 w-6 group-data-[open]:block"
                        />
                      </DisclosureButton>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Nav (Disclosure Panel) */}
              <DisclosurePanel className="md:hidden bg-white">
                <div className="mx-auto max-w-[110rem] px-4 pb-6 pt-4 sm:px-6 lg:px-10">
                                      <div className="divide-y divide-[#171A20]/10">
                    {/* Navigation Links */}
                    <div className="space-y-2 py-4">
                      {navigation.map((item) => (
                        <DisclosureButton
                          key={item.name}
                          as="a"
                          href={item.href}
                          aria-current={pathname === item.href ? 'page' : undefined}
                          className={classNames(
                            pathname === item.href
                              ? 'bg-[#171A20]/5'
                              : 'hover:bg-[#171A20]/5',
                            'flex items-center justify-between rounded-lg px-4 py-3 text-base font-bold transition-all duration-300 ease-out font-inter'
                          )}
                          style={{ color: '#171A20' }}
                        >
                          {item.name}
                          <ArrowRightIcon className="h-4 w-4 opacity-50" />
                        </DisclosureButton>
                      ))}
                    </div>


                    {/* Client Portal Button */}
                    <div className="pt-4">
                      <DisclosureButton
                        as="a"
                        href="/client-portal"
                        className="flex items-center justify-between rounded-lg px-4 py-3 text-base font-semibold hover:bg-[#171A20]/5 transition-all duration-300 ease-out font-inter"
                        style={{ backgroundColor: '#171A20', color: 'white' }}
                      >
                        <span>Client Portal</span>
                        <ArrowRightIcon className="h-4 w-4 opacity-70" />
                      </DisclosureButton>
                    </div>
                  </div>
                </div>
              </DisclosurePanel>
            </div>
          </div>
        )}
      </Disclosure>
    </>
  )
}
